import { create, type StateCreator, type StoreApi, type UseBoundStore } from 'zustand';
import { devtools } from 'zustand/middleware';

export interface LocalEdit {
  quantity?: number;
  isDeleted?: boolean;
  counterpartyProductId?: string;
}

export interface NewItem {
  tempId: string;
  counterparty_product_id: string;
  symbol_id: string;
  quantity: number;
}

export interface ReportEditState {
  selectedReportId: number | null;
  setSelectedReportId: (id: number | null) => void;
  selectedDate: string | null;
  setSelectedDate: (date: string | null) => void;

  isEditing: boolean;
  localEdits: Record<number, LocalEdit>;
  newItems: NewItem[];
  enterEditMode: () => void;
  cancelEdit: () => void;
  updateItemQuantity: (itemId: number, quantity: number, originalQuantity: number) => void;
  updateItemCounterpartyProductId: (itemId: number, value: string, originalValue: string) => void;
  toggleItemDeleted: (itemId: number) => void;
  markItemDeleted: (itemId: number) => void;
  addNewItem: (counterpartyProductId: string, symbolId: string, quantity: number) => void;
  removeNewItem: (tempId: string) => void;
  updateNewItemQuantity: (tempId: string, quantity: number) => void;
  updateNewItemCounterpartyProductId: (tempId: string, counterpartyProductId: string) => void;
  hasChanges: () => boolean;
  getChangedItems: (
    reportItems: Array<{ id: number; quantity: number; counterparty_product_id: string }>,
  ) => Array<{ id: number; quantity: number; is_deleted: boolean; counterparty_product_id?: string }>;
  getNewItemsPayload: () => Array<{ counterparty_product_id: string; symbol_id: string; quantity: number }>;
}

const reportEditSlice: StateCreator<ReportEditState, [], [], ReportEditState> = (set, get) => ({
  selectedReportId: null,
  setSelectedReportId: (id) => set({ selectedReportId: id }),
  selectedDate: null,
  setSelectedDate: (date) => set({ selectedDate: date }),

  isEditing: false,
  localEdits: {},
  newItems: [],

  enterEditMode: () => set({ isEditing: true, localEdits: {}, newItems: [] }),

  cancelEdit: () => set({ isEditing: false, localEdits: {}, newItems: [] }),

  updateItemQuantity: (itemId, quantity, originalQuantity) =>
    set((state) => {
      const existing = state.localEdits[itemId];
      if (quantity === originalQuantity && !existing?.isDeleted && existing?.counterpartyProductId === undefined) {
        const { [itemId]: _removed, ...rest } = state.localEdits;
        return { localEdits: rest };
      }
      return {
        localEdits: {
          ...state.localEdits,
          [itemId]: { ...existing, quantity },
        },
      };
    }),

  updateItemCounterpartyProductId: (itemId, value, originalValue) =>
    set((state) => {
      const existing = state.localEdits[itemId];
      if (value === originalValue && existing?.quantity === undefined && !existing?.isDeleted) {
        const { [itemId]: _removed, ...rest } = state.localEdits;
        return { localEdits: rest };
      }
      return {
        localEdits: {
          ...state.localEdits,
          [itemId]: { ...existing, counterpartyProductId: value },
        },
      };
    }),

  toggleItemDeleted: (itemId) =>
    set((state) => {
      const existing = state.localEdits[itemId];
      const newIsDeleted = !existing?.isDeleted;
      if (!newIsDeleted && existing?.quantity === undefined && existing?.counterpartyProductId === undefined) {
        const { [itemId]: _removed, ...rest } = state.localEdits;
        return { localEdits: rest };
      }
      return {
        localEdits: {
          ...state.localEdits,
          [itemId]: { ...existing, isDeleted: newIsDeleted },
        },
      };
    }),

  markItemDeleted: (itemId) =>
    set((state) => ({
      localEdits: {
        ...state.localEdits,
        [itemId]: { ...state.localEdits[itemId], isDeleted: true },
      },
    })),

  addNewItem: (counterpartyProductId, symbolId, quantity) =>
    set((state) => ({
      newItems: [
        ...state.newItems,
        {
          tempId: crypto.randomUUID(),
          counterparty_product_id: counterpartyProductId,
          symbol_id: symbolId,
          quantity,
        },
      ],
    })),

  removeNewItem: (tempId) =>
    set((state) => ({
      newItems: state.newItems.filter((item) => item.tempId !== tempId),
    })),

  updateNewItemQuantity: (tempId, quantity) =>
    set((state) => ({
      newItems: state.newItems.map((item) =>
        item.tempId === tempId ? { ...item, quantity } : item,
      ),
    })),

  updateNewItemCounterpartyProductId: (tempId, counterpartyProductId) =>
    set((state) => ({
      newItems: state.newItems.map((item) =>
        item.tempId === tempId ? { ...item, counterparty_product_id: counterpartyProductId } : item,
      ),
    })),

  hasChanges: () => Object.keys(get().localEdits).length > 0 || get().newItems.length > 0,

  getChangedItems: (reportItems) => {
    const itemMap = new Map(reportItems.map((i) => [i.id, i]));
    return Object.entries(get().localEdits).map(([idStr, edit]) => {
      const id = Number(idStr);
      const original = itemMap.get(id);
      const payload: { id: number; quantity: number; is_deleted: boolean; counterparty_product_id?: string } = {
        id,
        quantity: edit.quantity ?? original?.quantity ?? 0,
        is_deleted: edit.isDeleted === true,
      };
      if (edit.counterpartyProductId !== undefined) {
        payload.counterparty_product_id = edit.counterpartyProductId;
      }
      return payload;
    });
  },

  getNewItemsPayload: () =>
    get().newItems.map(({ counterparty_product_id, symbol_id, quantity }) => ({
      counterparty_product_id,
      symbol_id,
      quantity,
    })),
});

export function createReportEditStore(config: {
  name: string;
}): UseBoundStore<StoreApi<ReportEditState>>;
export function createReportEditStore<Extra extends object>(config: {
  name: string;
  extraSlice: StateCreator<ReportEditState & Extra, [], [], Extra>;
}): UseBoundStore<StoreApi<ReportEditState & Extra>>;
export function createReportEditStore<Extra extends object>(config: {
  name: string;
  extraSlice?: StateCreator<ReportEditState & Extra, [], [], Extra>;
}) {
  return create<ReportEditState & Extra>()(
    devtools(
      (set, get, store) => ({
        // Cast is safe: reportEditSlice only writes keys declared on ReportEditState,
        // and `set` accepts Partial<ReportEditState & Extra> — a superset of what the
        // slice ever produces. The widening is needed so the two slices share one `set`.
        ...(reportEditSlice as unknown as StateCreator<ReportEditState & Extra, [], [], ReportEditState>)(
          set,
          get,
          store,
        ),
        ...(config.extraSlice?.(set, get, store) ?? ({} as Extra)),
      }),
      { name: config.name, enabled: import.meta.env.DEV },
    ),
  );
}
